import React, { useState, useEffect, useMemo } from 'react';
import {
    MessageSquare,
    Plus,
    CheckCircle2,
    AlertTriangle,
    RefreshCw,
    Search,
    X,
    IndianRupee,
    Zap,
    Layers,
    Edit2,
    FileText,
    ShieldCheck,
    PauseCircle,
} from 'lucide-react';
import Sidebar from '../../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../../component/b2c/navBar/navBar';
import { useMyContext } from '../../../../../context/MyContext';
import toast from 'react-hot-toast';

const WhatsAppRatesManager = () => {
    const token = localStorage.getItem("token");
    const cus_id = localStorage.getItem("cus_id");
    const { isDarkMode, isToggleSidebar, setIsToggleSidebar } = useMyContext();

    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'

    const [formData, setFormData] = useState({
        message_type: '',
        rate: '',
        description: '',
        status: 'Active'
    });
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertConfig, setAlertConfig] = useState({});

    // Quick suggestions for easy one-click selection
    const EVENT_PRESETS = [
        { type: 'shipment_create', label: 'Shipment Created', desc: 'Alert buyer when order is manifested' },
        { type: 'shipment_ofd', label: 'Out for Delivery', desc: 'Morning dispatch alert to consignee' },
        { type: 'shipment_delivered', label: 'Shipment Delivered', desc: 'Delivery confirmation with invoice/feedback' },
        { type: 'ndr_raised', label: 'NDR Alert', desc: 'Non-delivery exception alert with reattempt link' },
        { type: 'rto_initiated', label: 'RTO Initiated', desc: 'Return-to-origin trigger message' },
    ];

    useEffect(() => {
        fetchRates();
    }, []);

    const fetchRates = async () => {
        try {
            setLoading(true);
            const response = await fetch("https://api.pickupxpress.com/admin/whatsapp/rates", {
                method: "GET",
                headers: {
                    cus_id: cus_id,
                    "Content-Type": "application/json",
                    token: token,
                },
            });

            const data = await response.json();
            if (data.status === true) {
                setRates(data?.data || []);
            } else {
                toast.error(data.message || "Failed to load WhatsApp rates");
            }
        } catch (err) {
            toast.error("Network error while fetching rates");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!formData.message_type || !formData.rate) {
            toast.error("Please fill Message Type and Rate");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch("https://api.pickupxpress.com/admin/whatsapp/setRate", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    cus_id: cus_id,
                    token: token,
                },
                body: JSON.stringify({
                    ...(editingId ? { id: editingId } : {}),
                    message_type: formData.message_type.trim(),
                    rate: formData.rate,
                    description: formData.description.trim(),
                    status: editingId ? formData.status : 'Active'
                }),
            });

            const data = await response.json();
            if (data.status === true) {
                if (editingId) {
                    setRates((prev) =>
                        prev.map((rate) =>
                            rate.id === editingId ? { ...formData, id: editingId } : rate
                        )
                    );
                    toast.success("Rate updated successfully!");
                } else {
                    const newRate = { ...formData, id: data?.insertedId || Date.now() };
                    setRates((prev) => [...prev, newRate]);
                    toast.success("New rate created successfully!");
                }
                resetForm();
            } else {
                toast.error(data.message || "Failed to save rate");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong while saving rate");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = (rate, newStatus) => {
        setAlertConfig({
            title: `${newStatus === "Active" ? "Activate" : "Deactivate"} Messaging Rate?`,
            message: `Are you sure you want to ${newStatus === "Active" ? "enable" : "pause"} automated messaging charges for "${rate.message_type}"?`,
            confirmText: newStatus === "Active" ? "Activate Now" : "Deactivate",
            type: "confirm",
            actionType: newStatus === "Active" ? "activate" : "deactivate",
            onConfirm: () => updateStatus(rate, newStatus),
        });
        setShowAlert(true);
    };

    const updateStatus = async (rate, newStatus) => {
        setLoading(true);
        try {
            const response = await fetch("https://api.pickupxpress.com/admin/whatsapp/setRate", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    cus_id: cus_id,
                    token: token,
                },
                body: JSON.stringify({
                    id: rate.id,
                    message_type: rate.message_type,
                    rate: rate.rate,
                    description: rate.description,
                    status: newStatus,
                }),
            });

            const data = await response.json();
            if (data.status === true) {
                setRates((prev) =>
                    prev.map((r) => (r.id === rate.id ? { ...r, status: newStatus } : r))
                );
                toast.success(`Rate ${newStatus === "Active" ? "activated" : "deactivated"} successfully!`);
            } else {
                toast.error(data.message || "Failed to update rate status");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong while updating status");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (rate) => {
        setFormData({
            message_type: rate.message_type || '',
            rate: rate.rate || '',
            description: rate.description || '',
            status: rate.status || 'Active'
        });
        setEditingId(rate.id);
        setShowForm(true);
    };

    const resetForm = () => {
        setFormData({
            message_type: '',
            rate: '',
            description: '',
            status: 'Active'
        });
        setEditingId(null);
        setShowForm(false);
    };

    const formatEventName = (str) => {
        if (!str) return 'Custom Event';
        return str
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    // Filtered rates list
    const filteredRates = useMemo(() => {
        return rates.filter((item) => {
            const matchesSearch =
                !searchQuery.trim() ||
                item.message_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                String(item.rate).includes(searchQuery);

            const isActive = item.status === 'Active';
            if (activeTab === 'ACTIVE') return matchesSearch && isActive;
            if (activeTab === 'INACTIVE') return matchesSearch && !isActive;
            return matchesSearch;
        });
    }, [rates, searchQuery, activeTab]);

    // Summary Metrics
    const metrics = useMemo(() => {
        const total = rates.length;
        const active = rates.filter((r) => r.status === 'Active').length;
        const inactive = total - active;
        const totalSum = rates.reduce((acc, curr) => acc + (parseFloat(curr.rate) || 0), 0);
        const avg = total > 0 ? (totalSum / total).toFixed(2) : '0.00';
        return { total, active, inactive, avg };
    }, [rates]);

    // Smooth Toggle Switch Component
    const StatusSwitch = ({ checked, onChange, disabled = false }) => (
        <label className="inline-flex items-center gap-2 cursor-pointer select-none group">
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                className="sr-only peer"
            />
            <div
                className={`relative w-10 h-5 rounded-full transition-all duration-300 ${checked
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 shadow-sm shadow-emerald-500/30'
                        : 'bg-slate-300 dark:bg-slate-700'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center ${checked ? 'translate-x-5' : 'translate-x-0'
                        }`}
                >
                    {checked ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    )}
                </div>
            </div>
            <span
                className={`text-[11px] font-bold transition-colors ${checked
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
            >
                {checked ? 'Active' : 'Paused'}
            </span>
        </label>
    );

    return (
        <div className={`relative w-full max-w-full h-screen overflow-hidden flex flex-col transition-colors duration-300 ${isDarkMode ? 'dark bg-[#080D1A]' : 'bg-slate-100'}`}>
            {/* Ambient background glows strictly contained */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
                <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-emerald-500/10 dark:bg-emerald-500/15 blur-3xl" />
                <div className="absolute top-1/2 -right-24 h-96 w-96 rounded-full bg-indigo-500/10 dark:bg-indigo-500/15 blur-3xl" />
            </div>

            {/* Top Navbar */}
            <NavBar />

            <div className="flex flex-1 min-h-0 min-w-0 w-full overflow-hidden relative z-10">
                {isToggleSidebar && (
                    <div
                        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
                        onClick={() => setIsToggleSidebar(false)}
                    />
                )}

                {/* Sidebar */}
                <div
                    className={`
            fixed inset-y-0 left-0 z-50 w-64
            ${isToggleSidebar ? "translate-x-0" : "-translate-x-full"}
            md:static md:inset-auto md:z-auto md:translate-x-0
            ${isToggleSidebar ? "md:w-64" : "md:w-[72px]"}
            m-2 sm:m-3 rounded-3xl overflow-hidden transition-all duration-300 ease-in-out shrink-0
          `}
                >
                    <Sidebar />
                </div>

                {/* Main Content Area */}
                <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden p-2 sm:p-2.5 md:p-3">
                    <div
                        className={`flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden rounded-2xl border p-2.5 sm:p-3 md:p-3.5 transition-all duration-300 shadow-sm ${isDarkMode
                                ? 'bg-gradient-to-br from-[#111827] via-[#0F172A] to-[#0B1120] border-slate-800 shadow-[0_0_40px_rgba(0,0,0,0.5)]'
                                : 'bg-white border-slate-200/90 shadow-lg shadow-slate-100'
                            }`}
                    >
                        {/* 1. TOP TOOLBAR: HEADER + SEARCH + ADD CTA */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3 shrink-0">
                            {/* Left: Icon, Title & Badge */}
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-emerald-500 via-teal-500 to-indigo-600 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-emerald-500/25 ring-2 ring-white/20">
                                    <MessageSquare size={20} className="stroke-[2.2]" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h1 className="text-base sm:text-lg font-black tracking-tight text-slate-900 dark:text-white">
                                            WhatsApp Communication Rates
                                        </h1>
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-bold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/80">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                            Live Gateway
                                        </span>
                                    </div>
                                    <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">
                                        Configure messaging tariffs, trigger events & notification charges for automated merchant alerts
                                    </p>
                                </div>
                            </div>

                            {/* Right: Search + Refresh + Add Rate */}
                            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                                {/* Search Bar */}
                                <div className="relative flex-1 sm:w-56 md:w-64">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search event, desc, rate..."
                                        className={`w-full pl-8 pr-7 py-1.5 text-xs rounded-xl border outline-none transition-all ${isDarkMode
                                                ? 'bg-slate-900/80 border-slate-700/80 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                                                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 shadow-2xs'
                                            }`}
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>

                                {/* Refresh */}
                                <button
                                    type="button"
                                    onClick={fetchRates}
                                    disabled={loading}
                                    className={`p-2 rounded-xl border text-slate-600 dark:text-slate-300 transition-all ${isDarkMode
                                            ? 'bg-slate-900/80 border-slate-700/80 hover:bg-slate-800'
                                            : 'bg-white border-slate-200 hover:bg-slate-50 shadow-2xs'
                                        }`}
                                    title="Refresh rates"
                                >
                                    <RefreshCw size={14} className={loading ? 'animate-spin text-emerald-500' : ''} />
                                </button>

                                {/* Add Rate CTA */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        resetForm();
                                        setShowForm(true);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 hover:from-emerald-500 hover:to-indigo-500 shadow-md shadow-emerald-500/25 active:scale-95 transition-all whitespace-nowrap"
                                >
                                    <Plus size={14} className="stroke-[2.5]" />
                                    <span>Add Rate</span>
                                </button>
                            </div>
                        </div>

                        {/* 2. STATS OVERVIEW CARDS */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3 shrink-0">
                            {/* Card 1: Total Configured */}
                            <div
                                className={`p-2.5 rounded-xl border flex items-center gap-2.5 transition-all ${isDarkMode
                                        ? 'bg-slate-900/50 border-slate-800/90'
                                        : 'bg-gradient-to-br from-slate-50 to-white border-slate-200/90'
                                    }`}
                            >
                                <div className="w-8 h-8 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-400 border border-teal-500/20 flex items-center justify-center shrink-0">
                                    <Layers size={16} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Total Triggers
                                    </p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-base font-black text-slate-900 dark:text-white">
                                            {metrics.total}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-medium">events</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Active */}
                            <div
                                className={`p-2.5 rounded-xl border flex items-center gap-2.5 transition-all ${isDarkMode
                                        ? 'bg-slate-900/50 border-slate-800/90'
                                        : 'bg-gradient-to-br from-emerald-50/50 to-white border-slate-200/90'
                                    }`}
                            >
                                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center justify-center shrink-0">
                                    <Zap size={16} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Active Rules
                                    </p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-base font-black text-emerald-600 dark:text-emerald-400">
                                            {metrics.active}
                                        </span>
                                        <span className="text-[10px] text-emerald-600/70 font-medium">running</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 3: Paused / Inactive */}
                            <div
                                className={`p-2.5 rounded-xl border flex items-center gap-2.5 transition-all ${isDarkMode
                                        ? 'bg-slate-900/50 border-slate-800/90'
                                        : 'bg-gradient-to-br from-amber-50/40 to-white border-slate-200/90'
                                    }`}
                            >
                                <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 flex items-center justify-center shrink-0">
                                    <PauseCircle size={16} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Paused Rules
                                    </p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-base font-black text-amber-600 dark:text-amber-400">
                                            {metrics.inactive}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-medium">inactive</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 4: Average Tariff */}
                            <div
                                className={`p-2.5 rounded-xl border flex items-center gap-2.5 transition-all ${isDarkMode
                                        ? 'bg-slate-900/50 border-slate-800/90'
                                        : 'bg-gradient-to-br from-indigo-50/50 to-white border-slate-200/90'
                                    }`}
                            >
                                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 flex items-center justify-center shrink-0">
                                    <IndianRupee size={16} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Average Tariff
                                    </p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-base font-black text-indigo-600 dark:text-indigo-400">
                                            ₹{metrics.avg}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-medium">/msg</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 3. SEGMENTED FILTER TABS */}
                        <div className="flex items-center justify-between gap-2 mb-2.5 shrink-0">
                            <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200/80 dark:border-slate-700/60">
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('ALL')}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${activeTab === 'ALL'
                                            ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-2xs'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                        }`}
                                >
                                    All Rates ({metrics.total})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('ACTIVE')}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${activeTab === 'ACTIVE'
                                            ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-emerald-600'
                                        }`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                    Active ({metrics.active})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('INACTIVE')}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${activeTab === 'INACTIVE'
                                            ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/30'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-amber-600'
                                        }`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                    Paused ({metrics.inactive})
                                </button>
                            </div>

                            <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 hidden sm:inline">
                                Showing {filteredRates.length} of {metrics.total} configurations
                            </span>
                        </div>

                        {/* 4. DESKTOP DATA TABLE (hidden below md) */}
                        <div
                            className={`hidden md:flex flex-1 min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border transition-colors duration-300 ${isDarkMode
                                    ? 'bg-slate-900/40 border-slate-800/90'
                                    : 'bg-white border-slate-200 shadow-2xs'
                                }`}
                        >
                            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-auto custom-scrollbar">
                                <table className="w-full table-fixed min-w-[760px] border-collapse text-left">
                                    <thead
                                        className={`sticky top-0 z-10 text-[10.5px] font-bold uppercase tracking-wider backdrop-blur-md transition-colors duration-300 ${isDarkMode
                                                ? 'bg-slate-900/95 text-slate-300 border-b border-slate-800'
                                                : 'bg-slate-50/95 text-slate-600 border-b border-slate-200'
                                            }`}
                                    >
                                        <tr>
                                            <th className="py-2.5 px-3.5 w-[32%]">Message Trigger / Event</th>
                                            <th className="py-2.5 px-3 w-[18%]">Tariff Rate</th>
                                            <th className="py-2.5 px-3 w-[28%]">Description & Scope</th>
                                            <th className="py-2.5 px-3 w-[12%] text-center">Status</th>
                                            <th className="py-2.5 px-4 w-[10%] text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                                        {loading && rates.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="py-16 text-center">
                                                    <div className="flex flex-col items-center justify-center gap-2">
                                                        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                                                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Loading messaging rates...</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : filteredRates.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="py-16 text-center">
                                                    <div className="flex flex-col items-center justify-center gap-2">
                                                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 flex items-center justify-center">
                                                            <MessageSquare size={22} />
                                                        </div>
                                                        <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                                                            No WhatsApp rates found
                                                        </p>
                                                        <p className="text-xs text-slate-400 max-w-xs">
                                                            {searchQuery ? 'Try adjusting your search query.' : 'Click "Add Rate" to configure your first message tariff.'}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredRates.map((rate, index) => {
                                                const isActive = rate.status === 'Active';
                                                return (
                                                    <tr
                                                        key={rate.id || index}
                                                        className={`transition-colors duration-150 ${isDarkMode
                                                                ? 'hover:bg-emerald-500/[0.03]'
                                                                : 'hover:bg-emerald-50/40'
                                                            }`}
                                                    >
                                                        {/* 1. Message Trigger / Event */}
                                                        <td className="py-2.5 px-3.5">
                                                            <div className="flex items-center gap-2.5 min-w-0">
                                                                <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-500/15 to-teal-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 shadow-2xs">
                                                                    <MessageSquare size={14} className="stroke-[2.2]" />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <h4 className="font-extrabold text-xs text-slate-900 dark:text-white truncate">
                                                                        {formatEventName(rate.message_type)}
                                                                    </h4>
                                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                                        <span className="font-mono text-[10px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 px-1.5 py-0.2 rounded font-semibold border border-emerald-200 dark:border-emerald-800/60 truncate">
                                                                            {rate.message_type}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* 2. Tariff Rate */}
                                                        <td className="py-2.5 px-3 whitespace-nowrap">
                                                            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 shadow-2xs">
                                                                <IndianRupee size={12} className="text-emerald-600 dark:text-emerald-400" />
                                                                <span className="font-extrabold text-xs text-slate-900 dark:text-white font-mono">
                                                                    {Number(rate.rate).toFixed(2)}
                                                                </span>
                                                                <span className="text-[9.5px] text-slate-400 font-medium">/msg</span>
                                                            </div>
                                                        </td>

                                                        {/* 3. Description & Scope */}
                                                        <td className="py-2.5 px-3">
                                                            <p className="text-[11px] text-slate-600 dark:text-slate-300 truncate leading-snug" title={rate.description}>
                                                                {rate.description || 'No description provided'}
                                                            </p>
                                                        </td>

                                                        {/* 4. Status Toggle Switch */}
                                                        <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                                            <div className="flex items-center justify-center">
                                                                <StatusSwitch
                                                                    checked={isActive}
                                                                    onChange={(e) => {
                                                                        const newStatus = e.target.checked ? 'Active' : 'Inactive';
                                                                        handleStatusToggle(rate, newStatus);
                                                                    }}
                                                                />
                                                            </div>
                                                        </td>

                                                        {/* 5. Actions */}
                                                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleEdit(rate)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-bold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-300 dark:hover:border-emerald-700 transition-all shadow-2xs"
                                                                title="Edit Rate"
                                                            >
                                                                <Edit2 size={11} />
                                                                <span>Edit</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 5. MOBILE / TABLET CARDS VIEW (hidden on md and up) */}
                        <div className="md:hidden flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-2 pb-1">
                            {filteredRates.length === 0 ? (
                                <div className="py-12 text-center">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 flex items-center justify-center mx-auto mb-2">
                                        <MessageSquare size={18} />
                                    </div>
                                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200">No rates found</p>
                                </div>
                            ) : (
                                filteredRates.map((rate, index) => {
                                    const isActive = rate.status === 'Active';
                                    return (
                                        <div
                                            key={rate.id || index}
                                            className={`p-3 rounded-xl border transition-all ${isDarkMode
                                                    ? 'bg-slate-900/80 border-slate-800'
                                                    : 'bg-white border-slate-200 shadow-2xs'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                                                        <MessageSquare size={14} />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="font-bold text-xs text-slate-900 dark:text-white truncate">
                                                            {formatEventName(rate.message_type)}
                                                        </h4>
                                                        <span className="font-mono text-[9.5px] text-emerald-600 dark:text-emerald-400">
                                                            {rate.message_type}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 font-mono font-black text-xs text-emerald-700 dark:text-emerald-300">
                                                    ₹{Number(rate.rate).toFixed(2)}
                                                </div>
                                            </div>

                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">
                                                {rate.description || 'No description provided'}
                                            </p>

                                            <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                                                <StatusSwitch
                                                    checked={isActive}
                                                    onChange={(e) => {
                                                        const newStatus = e.target.checked ? 'Active' : 'Inactive';
                                                        handleStatusToggle(rate, newStatus);
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleEdit(rate)}
                                                    className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-bold rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                                                >
                                                    <Edit2 size={11} />
                                                    Edit
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* 6. BOTTOM FOOTER TOOLBAR */}
                        <div
                            className={`flex-shrink-0 mt-2.5 pt-2.5 border-t flex flex-col sm:flex-row justify-between items-center gap-2 transition-colors duration-300 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'
                                }`}
                        >
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-medium">
                                <ShieldCheck size={14} className="text-emerald-500" />
                                <span>
                                    All WhatsApp notifications dispatched via official Meta Cloud API partners.
                                </span>
                            </div>
                            <div className="text-[11px] text-slate-400 dark:text-slate-500 font-semibold">
                                {metrics.total} rates configured
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ================= MODAL: ADD / EDIT RATE ================= */}
            {showForm && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
                    onClick={resetForm}
                >
                    <div
                        className={`w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden transition-all duration-300 ${isDarkMode
                                ? 'bg-slate-900 border-slate-800 text-white shadow-black/80'
                                : 'bg-white border-slate-200 text-slate-900 shadow-xl'
                            }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-sm">
                                    {editingId ? <Edit2 size={15} /> : <Plus size={16} className="stroke-[2.5]" />}
                                </div>
                                <div>
                                    <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                                        {editingId ? 'Edit Messaging Rate' : 'Add New Messaging Rate'}
                                    </h3>
                                    <p className="text-[10.5px] text-slate-500 dark:text-slate-400">
                                        Define trigger type, pricing per notification & description
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 space-y-4">
                            {/* Quick Event Presets */}
                            {!editingId && (
                                <div>
                                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                                        Quick Preset Suggestions:
                                    </label>
                                    <div className="flex flex-wrap gap-1.5">
                                        {EVENT_PRESETS.map((preset) => (
                                            <button
                                                key={preset.type}
                                                type="button"
                                                onClick={() => {
                                                    setFormData({
                                                        ...formData,
                                                        message_type: preset.type,
                                                        description: preset.desc,
                                                    });
                                                }}
                                                className={`px-2.5 py-1 rounded-lg text-[10.5px] font-semibold border transition-all ${formData.message_type === preset.type
                                                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-xs'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-300'
                                                    }`}
                                            >
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Message Type */}
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                                    <MessageSquare size={13} className="text-emerald-500" />
                                    <span>Message Type Identifier</span>
                                    <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.message_type}
                                    onChange={(e) => setFormData({ ...formData, message_type: e.target.value })}
                                    placeholder="e.g. shipment_ofd or ndr_raise"
                                    className={`w-full px-3.5 py-2 text-xs font-mono rounded-xl border outline-none transition-all ${isDarkMode
                                            ? 'bg-slate-950 border-slate-700 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                                            : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                                        }`}
                                    required
                                />
                            </div>

                            {/* Rate input */}
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                                    <IndianRupee size={13} className="text-emerald-500" />
                                    <span>Tariff Rate (in ₹ per message)</span>
                                    <span className="text-rose-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-xs text-slate-400">
                                        ₹
                                    </div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.rate}
                                        onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                        placeholder="e.g. 2.50"
                                        className={`w-full pl-8 pr-3.5 py-2 text-xs font-mono font-bold rounded-xl border outline-none transition-all ${isDarkMode
                                                ? 'bg-slate-950 border-slate-700 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                                                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                                            }`}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                                    <FileText size={13} className="text-emerald-500" />
                                    <span>Description / Scope</span>
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Describe when and to whom this notification is sent..."
                                    rows={3}
                                    className={`w-full px-3.5 py-2 text-xs rounded-xl border outline-none resize-none transition-all ${isDarkMode
                                            ? 'bg-slate-950 border-slate-700 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                                            : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                                        }`}
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40">
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-4 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading || !formData.message_type || !formData.rate}
                                className="px-5 py-1.5 text-xs font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {loading ? 'Saving...' : editingId ? 'Update Rate' : 'Create Rate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= CONFIRMATION DIALOG ================= */}
            {showAlert && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150"
                    onClick={() => setShowAlert(false)}
                >
                    <div
                        className={`w-full max-w-sm rounded-2xl border p-5 shadow-2xl transition-all ${isDarkMode ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'
                            }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div
                                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${alertConfig.actionType === 'activate'
                                        ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-500 border border-emerald-500/20'
                                        : 'bg-amber-50 dark:bg-amber-950/60 text-amber-500 border border-amber-500/20'
                                    }`}
                            >
                                {alertConfig.actionType === 'activate' ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                            </div>
                            <div>
                                <h4 className="text-sm font-extrabold">{alertConfig.title}</h4>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-5 leading-relaxed">
                            {alertConfig.message}
                        </p>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowAlert(false)}
                                className="px-3.5 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (alertConfig.onConfirm) alertConfig.onConfirm();
                                    setShowAlert(false);
                                }}
                                className={`px-4 py-1.5 text-xs font-bold rounded-xl text-white shadow-sm transition-all ${alertConfig.actionType === 'activate'
                                        ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20'
                                        : 'bg-amber-600 hover:bg-amber-500 shadow-amber-500/20'
                                    }`}
                            >
                                {alertConfig.confirmText || 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsAppRatesManager;